import { useNetwork } from '@vueuse/core';
import type { PaymentInitOptions } from '../interfaces/paymentInit.interface';
import type { PaymentInitStatus } from '../types/paymentInit';
import { usePaymentInitReport } from './usePaymentInitReport';
import {
  PAYMENT_INIT_ATTEMPTS,
  PAYMENT_INIT_RETRY_DELAY_MS,
  PAYMENT_INIT_RETRY_FACTOR,
  PAYMENT_INIT_TIMEOUT_MS,
} from '../common/constants/paymentInit';
import { retryPaymentInit } from '../utils/paymentInitRetry';

export function usePaymentInit(options: PaymentInitOptions) {
  const { isOnline } = useNetwork();
  const { reportPaymentInit } = usePaymentInitReport();

  const status = ref<PaymentInitStatus>('loading');
  const isReady = computed(() => status.value === 'ready');
  const isFailed = computed(() => status.value === 'failed');
  const isOffline = computed(() => !isOnline.value);

  let pending: { key: string, run: Promise<void> } | null = null;
  let hasFailedBefore = false;
  let cameBackOnlineDuringRun = false;
  let isDisposed = false;

  onScopeDispose(() => {
    isDisposed = true;
  });

  function requestKey(): string {
    return options.requestKey?.() ?? '';
  }

  function fail(attempts: number, error?: unknown): void {
    status.value = 'failed';
    hasFailedBefore = true;

    reportPaymentInit({
      outcome: options.blocksCheckout ? 'failed' : 'unavailable',
      action: options.action,
      attempts,
      error,
    });
  }

  async function attempt(key: string): Promise<void> {
    if (requestKey() !== key) return;

    status.value = 'loading';
    cameBackOnlineDuringRun = false;
    options.beforeRun?.();

    const result = await retryPaymentInit(options.initialize, {
      attempts: PAYMENT_INIT_ATTEMPTS,
      attemptTimeoutMs: options.attemptTimeoutMs ?? PAYMENT_INIT_TIMEOUT_MS,
      delayMs: PAYMENT_INIT_RETRY_DELAY_MS,
      delayFactor: PAYMENT_INIT_RETRY_FACTOR,
      isAbandoned: () => isDisposed,
      isOnline: () => isOnline.value,
      onRetry: () => {
        status.value = 'reconnecting';
      },
    });

    if (pending?.key === key) pending = null;

    if (isDisposed) return;

    if (!result.ok) {
      fail(result.attempts, result.error);

      if (!isOnline.value) return;

      if (cameBackOnlineDuringRun) {
        void load();
        return;
      }

      options.onGiveUp?.(result.error);

      return;
    }

    status.value = 'ready';

    if (result.attempts > 1 || hasFailedBefore) {
      reportPaymentInit({
        outcome: 'recovered',
        action: options.action,
        attempts: result.attempts,
      });
      hasFailedBefore = false;
    }
  }

  function load(): Promise<void> {
    if (isDisposed) return Promise.resolve();

    if (isOffline.value) {
      if (!isFailed.value) fail(0);

      return Promise.resolve();
    }

    const key = requestKey();

    if (pending?.key === key) return pending.run;

    const run = (pending?.run ?? Promise.resolve()).then(() => attempt(key));

    pending = { key, run };

    void run.finally(() => {
      if (pending?.run === run) pending = null;
    });

    return run;
  }

  watch(isOnline, (online) => {
    if (!online) return;

    if (isFailed.value) {
      void load();
      return;
    }

    cameBackOnlineDuringRun = true;
  });

  return { status, isReady, isFailed, isOffline, load };
}
